import { Component, OnInit, OnDestroy } from '@angular/core'
import { Firestore, collection, collectionData, query, orderBy, limit, where, doc, updateDoc, writeBatch, getDocs } from '@angular/fire/firestore'
import { FirestoreStructure } from '@parkupp/core'
import { Subscription } from 'rxjs'
import Swal from 'sweetalert2'

interface AdmytApiLog {
    $key?: string
    endpoint: string
    method: string
    requestBody?: string
    responseStatus: number
    responseBody?: string
    isError: boolean
    subscriptionId?: string
    clientId?: string
    centreId?: string
    tenantId?: string
    timestamp: any
    durationMs?: number
    errorMessage?: string
    viewed?: boolean
}

@Component({
    selector: 'app-admin-list-admyt-logs',
    templateUrl: './admin-list-admyt-logs.component.html',
    styleUrls: ['./admin-list-admyt-logs.component.scss'],
})
export class AdminListAdmytLogsComponent implements OnInit, OnDestroy {
    allLogs: AdmytApiLog[] = []
    loading = true
    error: string | null = null
    Math = Math

    private logsSub: Subscription | null = null

    // Pagination
    currentPage = 1
    itemsPerPage = 25

    // Filtering
    errorFilter: 'all' | 'errors' | 'success' = 'all'
    endpointFilter = ''
    viewedFilter: 'unviewed' | 'viewed' | 'all' = 'unviewed'

    // Expanded rows
    expandedLogIds: Set<string> = new Set()

    // Batch action
    markingAllViewed = false

    constructor(private firestore: Firestore) {}

    ngOnInit() {
        this.subscribeToLogs()
    }

    ngOnDestroy() {
        this.logsSub?.unsubscribe()
    }

    private subscribeToLogs() {
        this.logsSub?.unsubscribe()
        this.loading = true

        const logsCollection = collection(this.firestore, FirestoreStructure.ADMYT_API_LOGS)
        const q = query(logsCollection, orderBy('timestamp', 'desc'), limit(500))

        this.logsSub = collectionData(q, { idField: '$key' }).subscribe({
            next: (docs: any[]) => {
                this.allLogs = docs as AdmytApiLog[]
                this.loading = false
            },
            error: (err) => {
                console.error('Failed to load logs:', err)
                this.error = 'Failed to load API logs.'
                this.loading = false
            },
        })
    }

    get filteredLogs(): AdmytApiLog[] {
        return this.allLogs.filter((log) => {
            if (this.viewedFilter === 'unviewed' && log.viewed) return false
            if (this.viewedFilter === 'viewed' && !log.viewed) return false
            if (this.errorFilter === 'errors' && !log.isError) return false
            if (this.errorFilter === 'success' && log.isError) return false
            if (this.endpointFilter.trim() && !log.endpoint?.includes(this.endpointFilter.trim())) return false
            return true
        })
    }

    get totalItems(): number { return this.filteredLogs.length }
    get totalPages(): number { return Math.ceil(this.totalItems / this.itemsPerPage) }
    get unviewedCount(): number { return this.allLogs.filter((l) => !l.viewed).length }
    get errorCount(): number { return this.filteredLogs.filter((l) => l.isError).length }
    get successCount(): number { return this.filteredLogs.filter((l) => !l.isError).length }

    get paginatedLogs(): AdmytApiLog[] {
        const start = (this.currentPage - 1) * this.itemsPerPage
        return this.filteredLogs.slice(start, start + this.itemsPerPage)
    }

    onFilterChange() {
        this.currentPage = 1
    }

    goToPage(page: number) {
        if (page >= 1 && page <= this.totalPages) this.currentPage = page
    }

    getPageNumbers(): number[] {
        const pages: number[] = []
        const maxPages = 5
        const half = Math.floor(maxPages / 2)
        let start = Math.max(1, this.currentPage - half)
        let end = Math.min(this.totalPages, this.currentPage + half)
        if (end - start + 1 < maxPages) {
            if (start === 1) end = Math.min(this.totalPages, start + maxPages - 1)
            else if (end === this.totalPages) start = Math.max(1, end - maxPages + 1)
        }
        for (let i = start; i <= end; i++) pages.push(i)
        return pages
    }

    async toggleViewed(log: AdmytApiLog) {
        if (!log.$key) return
        const logRef = doc(this.firestore, FirestoreStructure.ADMYT_API_LOGS, log.$key)
        await updateDoc(logRef, { viewed: !log.viewed })
    }

    async markAllAsViewed() {
        const unviewed = this.allLogs.filter((l) => !l.viewed)
        if (unviewed.length === 0) return

        this.markingAllViewed = true
        try {
            // Firestore batch max is 500
            const batchSize = 500
            for (let i = 0; i < unviewed.length; i += batchSize) {
                const batch = writeBatch(this.firestore)
                const chunk = unviewed.slice(i, i + batchSize)
                for (const log of chunk) {
                    if (log.$key) {
                        batch.update(doc(this.firestore, FirestoreStructure.ADMYT_API_LOGS, log.$key), { viewed: true })
                    }
                }
                await batch.commit()
            }
        } catch (err) {
            console.error('Failed to mark all as viewed:', err)
        } finally {
            this.markingAllViewed = false
        }
    }

    toggleExpand(logId: string | undefined) {
        if (!logId) return
        if (this.expandedLogIds.has(logId)) {
            this.expandedLogIds.delete(logId)
        } else {
            this.expandedLogIds.add(logId)
        }
    }

    isExpanded(logId: string | undefined): boolean {
        return logId ? this.expandedLogIds.has(logId) : false
    }

    getMethodBadgeClass(method: string): string {
        switch (method?.toUpperCase()) {
            case 'GET': return 'badge bg-primary'
            case 'POST': return 'badge bg-success'
            case 'PUT': return 'badge bg-warning text-dark'
            case 'DELETE': return 'badge bg-danger'
            default: return 'badge bg-secondary'
        }
    }

    getStatusBadgeClass(status: number, isError: boolean): string {
        if (isError || status >= 400) return 'badge bg-danger'
        if (status >= 200 && status < 300) return 'badge bg-success'
        if (status >= 300 && status < 400) return 'badge bg-warning text-dark'
        return 'badge bg-secondary'
    }

    formatTimestamp(timestamp: any): string {
        if (!timestamp) return 'N/A'
        try {
            const date = timestamp._seconds ? new Date(timestamp._seconds * 1000) : timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp)
            return date.toLocaleString()
        } catch {
            return 'Invalid date'
        }
    }

    formatDuration(durationMs: number | undefined): string {
        if (durationMs === undefined) return 'N/A'
        if (durationMs < 1000) return `${durationMs}ms`
        return `${(durationMs / 1000).toFixed(2)}s`
    }

    formatJson(jsonString: string | undefined): string {
        if (!jsonString) return 'N/A'
        try {
            return JSON.stringify(JSON.parse(jsonString), null, 2)
        } catch {
            return jsonString
        }
    }

    truncateEndpoint(endpoint: string, maxLength: number = 40): string {
        if (!endpoint) return ''
        return endpoint.length <= maxLength ? endpoint : endpoint.substring(0, maxLength) + '...'
    }

    getAverageDuration(): string {
        const withDuration = this.filteredLogs.filter((l) => l.durationMs !== undefined)
        if (withDuration.length === 0) return 'N/A'
        const total = withDuration.reduce((sum, l) => sum + (l.durationMs || 0), 0)
        return this.formatDuration(Math.round(total / withDuration.length))
    }

    getSeverity(log: AdmytApiLog): 'success' | 'warning' | 'error' {
        if (log.isError || log.responseStatus >= 500) return 'error'
        if (log.responseStatus >= 400) return 'warning'
        return 'success'
    }

    getSeverityClass(log: AdmytApiLog): string {
        switch (this.getSeverity(log)) {
            case 'error': return 'admyt-status admyt-status--error'
            case 'warning': return 'admyt-status admyt-status--pending'
            default: return 'admyt-status admyt-status--active'
        }
    }

    async copyToClipboard(jsonString: string | undefined, label: string) {
        if (!jsonString) return
        try {
            await navigator.clipboard.writeText(this.formatJson(jsonString))
            Swal.fire({ title: `${label} copied`, icon: 'success', timer: 1000, showConfirmButton: false })
        } catch {
            Swal.fire({ title: 'Copy failed', icon: 'error', timer: 1000, showConfirmButton: false })
        }
    }

    trackByLogId(index: number, log: AdmytApiLog): string {
        return log.$key || index.toString()
    }
}
